// src/pages/NuevaNotaPage.tsx
import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import api from '../services/api'
import { useAuthStore } from '../store/authStore'
import { Search, Plus, Trash2, Save, ArrowLeft } from 'lucide-react'

interface Cliente {
  id_cliente: number
  ci_ruc: string
  nombre: string
}

interface Producto {
  id_producto: number
  detalle: string
  producto_nombre?: string
  codigo_barras: string
  precio_venta_a: number
  precio_venta_b: number
  precio_venta_c: number
  cantidad: number
  cantidad_disponible?: number
  iva: string
}

interface ItemVenta {
  id_producto: number
  nombre: string
  cantidad: number
  precio_unitario: number
  precio_venta_a: number
  precio_venta_b: number
  precio_venta_c: number
  iva: string
  subtotal: number
  stockDisponible: number
}

export function NuevaNotaPage() {
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const [clientes, setClientes] = useState<Cliente[]>([])
  const [productos, setProductos] = useState<Producto[]>([])
  const [clienteSeleccionado, setClienteSeleccionado] = useState<number | null>(null)
  const [items, setItems] = useState<ItemVenta[]>([])
  const [busquedaProducto, setBusquedaProducto] = useState('')
  const [busquedaCliente, setBusquedaCliente] = useState('')
  const [mostrarClientes, setMostrarClientes] = useState(false)
  const [loading, setLoading] = useState(false)
  const [descuento, setDescuento] = useState(0)

  const esDistribuidor = user?.rol === 'DISTRIBUIDOR'

  useEffect(() => {
    const fetchData = async () => {
      try {
        const clientesRes = await api.get('/clientes?limit=1000')
        setClientes(clientesRes.data.data || [])
        
        // Si es distribuidor, cargar su inventario asignado
        // Si es admin, cargar productos generales
        if (esDistribuidor) {
          const invRes = await api.get('/inventario/mi-inventario')
          const productosDistribuidor = invRes.data.data.map((item: any) => ({
            id_producto: item.id_producto,
            detalle: item.producto_nombre,
            codigo_barras: item.codigo_barras,
            precio_venta_a: item.precio_venta_a,
            precio_venta_b: item.precio_venta_b || item.precio_venta_a,
            precio_venta_c: item.precio_venta_c || item.precio_venta_a,
            cantidad: item.cantidad_disponible,
            cantidad_disponible: item.cantidad_disponible,
            iva: item.iva
          }))
          setProductos(productosDistribuidor)
        } else {
          const productosRes = await api.get('/productos')
          setProductos(productosRes.data.data || [])
        }
      } catch (error) {
        console.error('Error cargando datos:', error)
      }
    }
    fetchData()
  }, [esDistribuidor])

  const productosFiltrados = productos.filter(p =>
    p.detalle?.toLowerCase().includes(busquedaProducto.toLowerCase()) ||
    p.codigo_barras?.toLowerCase().includes(busquedaProducto.toLowerCase())
  )

  const clientesFiltrados = clientes.filter(c =>
    c.nombre?.toLowerCase().includes(busquedaCliente.toLowerCase()) ||
    c.ci_ruc?.toLowerCase().includes(busquedaCliente.toLowerCase())
  )

  const clienteSeleccionadoObj = clientes.find(c => c.id_cliente === clienteSeleccionado)

  const agregarProducto = (producto: Producto) => {
    const stockDisponible = producto.cantidad_disponible ?? producto.cantidad
    const existente = items.find(i => i.id_producto === producto.id_producto)
    
    if (existente) {
      // Verificar stock
      if (existente.cantidad >= stockDisponible) {
        alert(`Stock insuficiente. Disponible: ${stockDisponible}`)
        return
      }
      setItems(items.map(i => 
        i.id_producto === producto.id_producto
          ? { ...i, cantidad: i.cantidad + 1, subtotal: (i.cantidad + 1) * i.precio_unitario }
          : i
      ))
    } else {
      if (stockDisponible <= 0) {
        alert('Producto sin stock disponible')
        return
      }
      setItems([...items, {
        id_producto: producto.id_producto,
        nombre: producto.detalle,
        cantidad: 1,
        precio_unitario: producto.precio_venta_a,
        precio_venta_a: producto.precio_venta_a,
        precio_venta_b: producto.precio_venta_b,
        precio_venta_c: producto.precio_venta_c,
        iva: producto.iva || '12',
        subtotal: producto.precio_venta_a,
        stockDisponible
      }])
    }
    setBusquedaProducto('')
  }

  const actualizarCantidad = (idProducto: number, cantidad: number) => {
    if (cantidad <= 0) {
      eliminarItem(idProducto)
      return
    }
    const item = items.find(i => i.id_producto === idProducto)
    if (item && cantidad > item.stockDisponible) {
      alert(`Stock insuficiente. Disponible: ${item.stockDisponible}`)
      return
    }
    setItems(items.map(i =>
      i.id_producto === idProducto
        ? { ...i, cantidad, subtotal: cantidad * i.precio_unitario }
        : i
    ))
  }

  const actualizarPrecio = (idProducto: number, precio: number) => {
    setItems(items.map(i =>
      i.id_producto === idProducto
        ? { ...i, precio_unitario: precio, subtotal: i.cantidad * precio }
        : i
    ))
  }

  const eliminarItem = (idProducto: number) => {
    setItems(items.filter(i => i.id_producto !== idProducto))
  }

  // Cálculos
  const subtotal12 = items
    .filter(i => i.iva === '12')
    .reduce((sum, i) => sum + i.subtotal, 0)
  
  const subtotal0 = items
    .filter(i => i.iva !== '12')
    .reduce((sum, i) => sum + i.subtotal, 0)
  
  const iva = subtotal12 * 0.12
  const total = subtotal0 + subtotal12 + iva - descuento

  const handleGuardar = async () => {
    if (items.length === 0) {
      alert('Debe agregar al menos un producto')
      return
    }

    setLoading(true)
    try {
      const payload = {
        id_cliente: clienteSeleccionado,
        descuento,
        items: items.map(i => ({
          id_producto: i.id_producto,
          cantidad: i.cantidad,
          precio_unitario: i.precio_unitario
        }))
      }

      const { data } = await api.post('/notas-venta', payload)
      alert(`Nota de venta ${data.num_fact} creada exitosamente`)
      navigate('/notas-venta')
    } catch (error) {
      console.error('Error creando nota:', error)
      alert('Error al crear la nota de venta')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <button
            onClick={() => navigate('/notas-venta')}
            className="mr-4 p-2 hover:bg-gray-100 rounded-lg"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-2xl font-bold text-gray-800">Nueva Nota de Venta</h1>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Panel izquierdo - Productos */}
        <div className="lg:col-span-2 space-y-4">
          {/* Buscar producto */}
          <div className="bg-white rounded-xl shadow-sm p-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Buscar producto por nombre o código..."
                value={busquedaProducto}
                onChange={(e) => setBusquedaProducto(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
            
            {/* Lista de productos filtrados */}
            {busquedaProducto && (
              <div className="mt-2 max-h-60 overflow-y-auto border rounded-lg divide-y bg-white z-10 relative">
                {productosFiltrados.length === 0 ? (
                  <div className="px-4 py-3 text-gray-500 text-sm">No se encontraron productos</div>
                ) : (
                  productosFiltrados.slice(0, 10).map(producto => (
                    <button
                      key={producto.id_producto}
                      onClick={() => agregarProducto(producto)}
                      className="w-full px-4 py-3 text-left hover:bg-gray-50 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2"
                    >
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{producto.detalle}</p>
                        <p className="text-sm text-gray-500 truncate">{producto.codigo_barras || 'Sin código'}</p>
                      </div>
                      <div className="text-left sm:text-right flex-shrink-0">
                        <p className="font-medium">${producto.precio_venta_a.toFixed(2)}</p>
                        <p className="text-sm text-gray-500">Stock: {producto.cantidad}</p>
                      </div>
                    </button>
                  ))
                )}
              </div>
            )}
          </div>

          {/* Items agregados */}
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b">
              <h2 className="font-semibold">Items de la venta</h2>
            </div>
            
            {items.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-48 text-gray-500">
                <Plus className="w-12 h-12 mb-2" />
                <p>Agregue productos a la venta</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Producto</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Cant</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">P. Unit</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Subtotal</th>
                      <th className="px-4 py-3"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {items.map(item => (
                      <tr key={item.id_producto}>
                        <td className="px-4 py-3">
                          <p className="font-medium">{item.nombre}</p>
                          <p className="text-xs text-gray-500">IVA {item.iva}%</p>
                        </td>
                        <td className="px-4 py-3">
                          <input
                            type="number"
                            min="1"
                            value={item.cantidad}
                            onChange={(e) => actualizarCantidad(item.id_producto, parseInt(e.target.value) || 0)}
                            className="w-16 px-2 py-1 border rounded text-center"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <div className="space-y-1">
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              value={item.precio_unitario}
                              onChange={(e) => actualizarPrecio(item.id_producto, parseFloat(e.target.value) || 0)}
                              className="w-full px-2 py-1 border rounded text-sm font-medium"
                              placeholder="Precio"
                            />
                            <div className="flex gap-1 text-xs">
                              <button
                                type="button"
                                onClick={() => actualizarPrecio(item.id_producto, item.precio_venta_a)}
                                className={`px-2 py-0.5 rounded ${item.precio_unitario === item.precio_venta_a ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                                title={`Mayorista: $${item.precio_venta_a.toFixed(2)}`}
                              >
                                Mayorista
                              </button>
                              <button
                                type="button"
                                onClick={() => actualizarPrecio(item.id_producto, item.precio_venta_b)}
                                className={`px-2 py-0.5 rounded ${item.precio_unitario === item.precio_venta_b ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                                title={`Minorista: $${item.precio_venta_b.toFixed(2)}`}
                              >
                                Minorista
                              </button>
                              <button
                                type="button"
                                onClick={() => actualizarPrecio(item.id_producto, item.precio_venta_c)}
                                className={`px-2 py-0.5 rounded ${item.precio_unitario === item.precio_venta_c ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                                title={`Público: $${item.precio_venta_c.toFixed(2)}`}
                              >
                                Público
                              </button>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right font-medium">
                          ${item.subtotal.toFixed(2)}
                        </td>
                        <td className="px-4 py-3">
                          <button
                            onClick={() => eliminarItem(item.id_producto)}
                            className="p-1 text-red-500 hover:bg-red-50 rounded"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Panel derecho - Cliente y totales */}
        <div className="space-y-4">
          {/* Cliente */}
          <div className="bg-white rounded-xl shadow-sm p-4">
            <h3 className="font-semibold mb-3">Cliente</h3>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 z-10" />
              <input
                type="text"
                placeholder="Buscar cliente o Consumidor Final..."
                value={clienteSeleccionado ? clienteSeleccionadoObj?.nombre || '' : busquedaCliente}
                onChange={(e) => {
                  setBusquedaCliente(e.target.value)
                  setClienteSeleccionado(null)
                  setMostrarClientes(true)
                }}
                onFocus={() => setMostrarClientes(true)}
                className="w-full pl-9 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
              {clienteSeleccionado && (
                <button
                  onClick={() => {
                    setClienteSeleccionado(null)
                    setBusquedaCliente('')
                    setMostrarClientes(true)
                  }}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              )}
            </div>
            
            {/* Lista de clientes filtrados */}
            {mostrarClientes && !clienteSeleccionado && (
              <div className="mt-2 max-h-48 overflow-y-auto border rounded-lg divide-y bg-white relative z-10">
                <button
                  onClick={() => {
                    setClienteSeleccionado(null)
                    setBusquedaCliente('')
                    setMostrarClientes(false)
                  }}
                  className="w-full px-4 py-2 text-left hover:bg-gray-50 text-sm"
                >
                  <span className="font-medium">Consumidor Final</span>
                </button>
                {clientesFiltrados.length === 0 && busquedaCliente ? (
                  <div className="px-4 py-2 text-gray-500 text-sm">No se encontraron clientes</div>
                ) : (
                  clientesFiltrados.map(cliente => (
                    <button
                      key={cliente.id_cliente}
                      onClick={() => {
                        setClienteSeleccionado(cliente.id_cliente)
                        setBusquedaCliente(cliente.nombre)
                        setMostrarClientes(false)
                      }}
                      className="w-full px-4 py-2 text-left hover:bg-gray-50"
                    >
                      <p className="font-medium text-sm">{cliente.nombre}</p>
                      <p className="text-xs text-gray-500">{cliente.ci_ruc}</p>
                    </button>
                  ))
                )}
              </div>
            )}
          </div>

          {/* Totales */}
          <div className="bg-white rounded-xl shadow-sm p-4">
            <h3 className="font-semibold mb-3">Resumen</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Subtotal 0%:</span>
                <span>${subtotal0.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Subtotal 12%:</span>
                <span>${subtotal12.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">IVA 12%:</span>
                <span>${iva.toFixed(2)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-500">Descuento:</span>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={descuento}
                  onChange={(e) => setDescuento(parseFloat(e.target.value) || 0)}
                  className="w-24 px-2 py-1 border rounded text-right"
                />
              </div>
              <hr className="my-2" />
              <div className="flex justify-between text-lg font-bold">
                <span>TOTAL:</span>
                <span>${total.toFixed(2)}</span>
              </div>
            </div>
          </div>

          {/* Botones */}
          <button
            onClick={handleGuardar}
            disabled={loading || items.length === 0}
            className="w-full bg-primary-600 text-white py-3 rounded-lg font-medium hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
          >
            {loading ? (
              <span>Procesando...</span>
            ) : (
              <>
                <Save className="w-5 h-5 mr-2" />
                Guardar Nota de Venta
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}
